import { useEffect, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { FiCheck } from "react-icons/fi";
import { RiDeleteBin6Line } from "react-icons/ri";
import { toast } from "react-toastify";
import axiosInstance from "../../axiosInstance";
import * as general from "../../libs/general";

const emptyForm = {
  id: null,
  name: "",
  username: "",
  email: "",
  phoneNumber: "",
  lastLoginAt: null,
};

const ChildUserEditor = ({ show, model, onClose, onSaved, parentUserId = null }) => {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(model?.id);

  useEffect(() => {
    if (!show) return;
    if (model?.id) {
      const lastLogin = model.lastLoginAt ?? model.last_login_at ?? null;
      setForm({
        id: model.id,
        name: model.name ?? "",
        username: model.username ?? "",
        email: model.email ?? "",
        phoneNumber: model.phoneNumber ?? "",
        lastLoginAt: lastLogin,
      });
    } else {
      setForm(emptyForm);
    }
    setErrors({});
  }, [show, model]);

  useEffect(() => {
    if (!show || model?.id == null || Number(model.id) <= 0) return;
    let cancelled = false;
    axiosInstance
      .get("/api/users/getUser", { params: { id: model.id } })
      .then((res) => {
        if (cancelled) return;
        const dto = res?.data?.data;
        if (!dto) return;
        const ts = dto.lastLoginAt ?? dto.last_login_at;
        if (ts != null) {
          setForm((prev) => ({ ...prev, lastLoginAt: ts }));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [show, model?.id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleResetPassword = () => {
    if (!form?.id || form.id <= 0) return;
    general.openConfirm(
      async () => {
        try {
          const response = await axiosInstance.get("/api/users/resetPassword", {
            params: { id: form.id },
          });
          if (!response.data?.success) {
            toast.error(response.data?.message || "Không thể đặt lại mật khẩu.");
            return;
          }
          toast.success(response.data.message || "Đã yêu cầu user đặt lại mật khẩu.");
        } catch (err) {
          toast.error(err?.response?.data?.message || err.message || "Không thể đặt lại mật khẩu.");
        }
      },
      "Bạn có chắc muốn yêu cầu user <b>đặt lại mật khẩu</b> để đăng nhập? " +
        "Lần đăng nhập tiếp theo, user sẽ phải thiết lập mật khẩu mới trước khi sử dụng hệ thống.",
      "Đặt lại mật khẩu",
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setErrors({});
    try {
      const payload = {
        name: form.name,
        username: form.username,
        email: form.email,
        phoneNumber: form.phoneNumber,
      };
      if (isEdit) {
        payload.id = Number(model.id);
      }
      const response = await axiosInstance.post("/api/users/saveChildUser", payload, {
        params: parentUserId ? { parentUserId } : undefined,
      });
      if (!response.data?.success) {
        const field = response.data?.name;
        const message = response.data?.message || (isEdit ? "Không cập nhật được user." : "Không tạo được user.");
        if (field) {
          setErrors({ [field]: message });
        }
        toast.error(message);
        return;
      }
      toast.success(
        response.data.message || (isEdit ? "Cập nhật user thành công." : "Tạo user thành công.")
      );
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err.message || (isEdit ? "Không cập nhật được user." : "Không tạo được user."));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (!isEdit || !model?.id) return;
    const displayName = (form.name || model.username || model.email || "").trim();
    general.openConfirm(
      async () => {
        try {
          const response = await axiosInstance.get("/api/users/deleteChildUser", {
            params: {
              id: model.id,
              ...(parentUserId ? { parentUserId } : {}),
            },
          });
          if (!response.data?.success) {
            toast.error(response.data?.message || "Không xóa được user.");
            return;
          }
          toast.success(response.data.message || "Đã xóa user thành công.");
          onSaved?.();
          onClose?.();
        } catch (err) {
          toast.error(err.message || "Không xóa được user.");
        }
      },
      `Bạn có chắc chắn muốn xóa user "${displayName}" không?`,
      "Xác nhận xóa"
    );
  };

  return (
    <Modal show={show} onHide={onClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? "Cập nhật user" : "Thêm user mới"}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>
              <span className="text-danger">*</span> Họ và tên
            </Form.Label>
            <Form.Control
              name="name"
              value={form.name}
              onChange={handleChange}
              isInvalid={Boolean(errors.name)}
            />
            <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Tên đăng nhập</Form.Label>
            <Form.Control
              name="username"
              value={form.username}
              onChange={handleChange}
              isInvalid={Boolean(errors.username)}
            />
            <Form.Control.Feedback type="invalid">{errors.username}</Form.Control.Feedback>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              isInvalid={Boolean(errors.email)}
            />
            <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
          </Form.Group>
          <Form.Group>
            <Form.Label>Điện thoại</Form.Label>
            <Form.Control
              name="phoneNumber"
              value={form.phoneNumber}
              onChange={handleChange}
            />
          </Form.Group>
          {!isEdit && (
            <p className="text-muted small mt-3 mb-0">
              User mới sẽ dùng chung dữ liệu với tài khoản của bạn và cần thiết lập mật khẩu trước khi đăng nhập.
            </p>
          )}
          {isEdit && (
            <span className="text-muted small d-block mt-3">
              Đăng nhập lần cuối:{" "}
              {form.lastLoginAt
                ? general.formatDateTime3(form.lastLoginAt)
                : "Chưa có lần đăng nhập ghi nhận"}
            </span>
          )}
        </Modal.Body>
        <Modal.Footer
          className={`d-flex align-items-center gap-2 ${
            isEdit ? "justify-content-between" : "justify-content-end"
          }`}
        >
          {isEdit && (
            <div className="d-flex align-items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline-danger"
                className="d-inline-flex align-items-center justify-content-center p-1"
                onClick={handleDelete}
                disabled={saving}
                title="Xóa user"
                aria-label="Xóa user"
              >
                <RiDeleteBin6Line size={16} />
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline-secondary"
                onClick={handleResetPassword}
                disabled={saving}
                title="Yêu cầu user đặt lại mật khẩu khi đăng nhập lần tiếp theo"
              >
                Đặt lại mật khẩu
              </Button>
            </div>
          )}
          <Button size="sm" variant="primary" type="submit" disabled={saving}>
            <FiCheck className="me-1" />
            {saving ? "Đang lưu…" : "Lưu"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ChildUserEditor;
